// Constantes e tipos da camada de Inteligência Comercial (Parte 1).
// Não substituem o CRM nem o motor financeiro — complementam o Centro de Comando.

export const STATUS_COMERCIAL: { key: string; label: string }[] = [
  { key: 'novo', label: 'Novo' },
  { key: 'em_andamento', label: 'Em andamento' },
  { key: 'falar_hoje', label: 'Falar hoje' },
  { key: 'falar_semana', label: 'Falar esta semana' },
  { key: 'aguardando_cliente', label: 'Aguardando cliente' },
  { key: 'proposta_enviada', label: 'Proposta enviada' },
  { key: 'negociacao', label: 'Negociação' },
  { key: 'fechado', label: 'Fechado' },
  { key: 'pos_venda', label: 'Pós-venda' },
  { key: 'nutrir_depois', label: 'Nutrir mais tarde' },
  { key: 'perdido', label: 'Perdido' },
  { key: 'nao_prospectar', label: 'Não prospectar' },
];

export const STATUS_COMERCIAL_LABELS: Record<string, string> = Object.fromEntries(
  STATUS_COMERCIAL.map((s) => [s.key, s.label]),
);

export const TIPO_RELACIONAMENTO: { key: string; label: string }[] = [
  { key: 'cliente_b2b', label: 'Cliente / Lead B2B' },
  { key: 'arquiteto_especificador', label: 'Arquiteto / Especificador' },
  { key: 'designer_especificador', label: 'Designer / Especificador' },
  { key: 'fornecedor_fabrica', label: 'Fornecedor / Fábrica' },
  { key: 'construtora_incorporadora', label: 'Construtora / Incorporadora' },
  { key: 'orgao_publico', label: 'Órgão público / Licitação' },
  { key: 'parceiro', label: 'Parceiro' },
  { key: 'prestador', label: 'Prestador' },
  { key: 'interno_pessoal', label: 'Interno / Pessoal' },
  { key: 'nao_prospectar', label: 'Não prospectar' },
  { key: 'outro', label: 'Outro' },
];

export const TIPO_RELACIONAMENTO_LABELS: Record<string, string> = Object.fromEntries(
  TIPO_RELACIONAMENTO.map((t) => [t.key, t.label]),
);

export const DECISOES_OVERRIDE: { key: string; label: string }[] = [
  { key: 'nao_prospectar', label: 'Não prospectar' },
  { key: 'nao_priorizar', label: 'Não priorizar agora' },
  { key: 'nao_arquiteto', label: 'Não classificar como arquiteto' },
  { key: 'priorizar', label: 'Priorizar' },
  { key: 'reativar', label: 'Reativar' },
  { key: 'monitorar', label: 'Monitorar' },
];

export const DECISOES_OVERRIDE_LABELS: Record<string, string> = Object.fromEntries(
  DECISOES_OVERRIDE.map((d) => [d.key, d.label]),
);

export const ORIGEM_WHATSAPP: { key: string; label: string }[] = [
  { key: 'whatsapp_normal', label: 'WhatsApp Normal' },
  { key: 'whatsapp_business', label: 'WhatsApp Business' },
];

export const ORIGEM_WHATSAPP_LABELS: Record<string, string> = Object.fromEntries(
  ORIGEM_WHATSAPP.map((o) => [o.key, o.label]),
);

// Rótulos dos scores de relacionamento
export const SCORE_INFO: Record<string, { label: string; icon: string; desc: string }> = {
  lead: { label: 'Score Lead', icon: 'ri-flashlight-line', desc: 'Quão quente é este contato como lead ativo.' },
  reativacao: { label: 'Score Reativação', icon: 'ri-restart-line', desc: 'Potencial de reativar um contato antigo.' },
  arquiteto: { label: 'Score Arquiteto/Especificador', icon: 'ri-pen-nib-line', desc: 'Potencial de geração de projetos.' },
  fornecedor: { label: 'Score Fornecedor', icon: 'ri-store-3-line', desc: 'Relevância estratégica do fornecedor.' },
};

// Classificação visual dos scores (transparente, nunca caixa-preta)
export function scoreTone(score: number): { label: string; tone: string } {
  if (score >= 85) return { label: 'AGIR AGORA', tone: 'bg-green-50 text-green-700 border-green-200' };
  if (score >= 70) return { label: 'PRIORIDADE', tone: 'bg-amber-50 text-amber-700 border-amber-200' };
  if (score >= 50) return { label: 'MONITORAR / DESENVOLVER', tone: 'bg-gray-100 text-gray-600 border-gray-200' };
  return { label: 'BAIXA PRIORIDADE', tone: 'bg-gray-50 text-gray-400 border-gray-100' };
}

// ---------------------------------------------------------------------------
// FILAS INTELIGENTES (Parte 2)
// ---------------------------------------------------------------------------

export interface FilaDef {
  key: string;
  label: string;
  icon: string;
  description: string;
  tone: string;
}

export const FILAS_DEFS: FilaDef[] = [
  { key: 'falar_hoje', label: 'Falar Hoje', icon: 'ri-sun-line', description: 'Follow-up vencido ou para hoje, propostas aguardando retorno e urgências.', tone: 'bg-red-50 text-red-600' },
  { key: 'falar_semana', label: 'Falar Esta Semana', icon: 'ri-calendar-check-line', description: 'Próxima ação nos próximos 7 dias e contas estratégicas.', tone: 'bg-amber-50 text-amber-700' },
  { key: 'leads_quentes', label: 'Leads Quentes', icon: 'ri-fire-line', description: 'Score Lead alto + sinais de compra (orçamento, projeto, B2B).', tone: 'bg-orange-50 text-orange-600' },
  { key: 'reativar', label: 'Clientes Antigos para Reativar', icon: 'ri-restart-line', description: 'Clientes sem contato recente com histórico de orçamento/negociação.', tone: 'bg-emerald-50 text-emerald-700' },
  { key: 'pediu_orcamento', label: 'Pediu Orçamento e Sumiu', icon: 'ri-file-list-3-line', description: 'Proposta/cotação enviada sem fechamento e silêncio posterior.', tone: 'bg-sky-50 text-sky-700' },
  { key: 'pediu_desconto', label: 'Pediu Desconto e Sumiu', icon: 'ri-percent-line', description: 'Pedido real de desconto sem fechamento e silêncio posterior.', tone: 'bg-violet-50 text-violet-700' },
  { key: 'projetos_corporativos', label: 'Projetos Corporativos', icon: 'ri-building-2-line', description: 'Oportunidades/projetos corporativos em andamento.', tone: 'bg-indigo-50 text-indigo-700' },
  { key: 'arquitetos', label: 'Arquitetos / Especificadores', icon: 'ri-pen-nib-line', description: 'Arquitetos e especificadores com potencial de geração de projetos.', tone: 'bg-teal-50 text-teal-700' },
  { key: 'clinicas', label: 'Clínicas', icon: 'ri-heart-pulse-line', description: 'Empresas do segmento de saúde com potencial comercial.', tone: 'bg-rose-50 text-rose-600' },
  { key: 'orgaos_publicos', label: 'Órgãos Públicos', icon: 'ri-government-line', description: 'Órgãos públicos e licitações identificados.', tone: 'bg-blue-50 text-blue-700' },
  { key: 'fornecedores_estrategicos', label: 'Fornecedores Estratégicos', icon: 'ri-store-3-line', description: 'Fornecedores com Score Fornecedor relevante.', tone: 'bg-cyan-50 text-cyan-700' },
  { key: 'fornecedores_esquecidos', label: 'Fornecedores Esquecidos', icon: 'ri-forbid-line', description: 'Fornecedores relevantes sem contato recente (padrão 180+ dias).', tone: 'bg-slate-100 text-slate-600' },
  { key: 'pos_venda', label: 'Pós-venda Pendente', icon: 'ri-service-line', description: 'Entregas, resoluções, ajustes e compromissos de retorno pendentes.', tone: 'bg-fuchsia-50 text-fuchsia-700' },
  { key: 'nao_prospectar', label: 'Não Prospectar', icon: 'ri-eye-off-line', description: 'Visão de transparência — decisões manuais de não prospecção.', tone: 'bg-gray-100 text-gray-500' },
  { key: 'relacionamentos_quentes', label: 'Relacionamentos Quentes', icon: 'ri-fire-line', description: 'Relacionamentos classificados como quentes/muito quentes — memória de relacionamento.', tone: 'bg-red-50 text-red-600' },
  { key: 'reunioes_pendentes', label: 'Reuniões Pendentes', icon: 'ri-calendar-2-line', description: 'Reuniões adiadas, agendadas ou a remarcar — porta aberta, não perdida.', tone: 'bg-orange-50 text-orange-600' },
];

export const FILA_INFO: Record<string, FilaDef> = Object.fromEntries(FILAS_DEFS.map((f) => [f.key, f]));

// ---------------------------------------------------------------------------
// FORNECEDORES (Parte 2)
// ---------------------------------------------------------------------------

export const TIPOS_FORNECEDOR: { key: string; label: string }[] = [
  { key: 'fabricante', label: 'Fabricante' },
  { key: 'importador', label: 'Importador' },
  { key: 'distribuidor', label: 'Distribuidor' },
  { key: 'revendedor', label: 'Revendedor' },
  { key: 'prestador', label: 'Prestador' },
  { key: 'outro', label: 'Outro' },
];

export const TIPOS_FORNECEDOR_LABELS: Record<string, string> = Object.fromEntries(
  TIPOS_FORNECEDOR.map((t) => [t.key, t.label]),
);

export const STATUS_RELACIONAMENTO: { key: string; label: string }[] = [
  { key: 'ativo', label: 'Ativo' },
  { key: 'monitorar', label: 'Monitorar' },
  { key: 'esquecido', label: 'Esquecido' },
  { key: 'nao_usar', label: 'Não usar' },
  { key: 'encerrado', label: 'Encerrado' },
];

export const STATUS_RELACIONAMENTO_LABELS: Record<string, string> = Object.fromEntries(
  STATUS_RELACIONAMENTO.map((s) => [s.key, s.label]),
);

// Categorias de perguntas para Inteligência de Conteúdo
export const CATEGORIAS_CONTEUDO: { key: string; label: string }[] = [
  { key: 'preco', label: 'Preço' },
  { key: 'pagamento', label: 'Pagamento' },
  { key: 'frete', label: 'Frete' },
  { key: 'prazo', label: 'Prazo' },
  { key: 'estoque', label: 'Estoque' },
  { key: 'garantia', label: 'Garantia' },
  { key: 'montagem', label: 'Montagem' },
  { key: 'projeto', label: 'Projeto' },
  { key: 'nota_fiscal', label: 'Nota fiscal / CNPJ' },
  { key: 'showroom', label: 'Showroom' },
  { key: 'acabamento', label: 'Acabamento' },
  { key: 'ergonomia', label: 'Ergonomia' },
  { key: 'acustica', label: 'Acústica' },
  { key: 'divisorias', label: 'Divisórias' },
  { key: 'carpete', label: 'Carpete' },
  { key: 'persianas', label: 'Persianas' },
  { key: 'outros', label: 'Outros' },
];

export const CATEGORIAS_CONTEUDO_LABELS: Record<string, string> = Object.fromEntries(
  CATEGORIAS_CONTEUDO.map((c) => [c.key, c.label]),
);

// Formato de item de fila (espelhado do backend filas-admin)
export interface FilaItem {
  key: string;
  fila: string;
  nome: string;
  empresa: string | null;
  cidade: string | null;
  uf: string | null;
  tipo: string | null;
  score: number | null;
  score_tipo: string | null;
  score_componentes: { key: string; label: string; max: number; pontos: number; evidencia: string | null }[] | null;
  score_insuficiente: boolean;
  ultimo_contato: string | null;
  dias_sem_contato: number | null;
  produtos: string[];
  resumo_ia: string | null;
  motivo: string;
  proxima_acao: string | null;
  proxima_acao_data: string | null;
  valor_potencial: number | null;
  lucro_potencial: number | null;
  contato_id: number | null;
  empresa_id: number | null;
  oportunidade_id: number | null;
  entidade_tipo: string;
  entidade_id: number;
  status_comercial: string | null;
  override: { decisao_manual: string; motivo: string | null } | null;
}

// ---------------------------------------------------------------------------
// RELACIONAMENTO OMNICHANNEL (Complemento — WhatsApp + Instagram + próximos passos)
// Temperatura e estágio são independentes de oportunidade financeira.
// ---------------------------------------------------------------------------

export const TEMPERATURA_RELACIONAMENTO: { key: string; label: string; emoji: string; tone: string }[] = [
  { key: 'muito_quente', label: 'Muito quente', emoji: '🔥', tone: 'bg-red-50 text-red-700 border-red-200' },
  { key: 'quente', label: 'Quente', emoji: '🟢', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'em_desenvolvimento', label: 'Em desenvolvimento', emoji: '🟡', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'frio', label: 'Frio', emoji: '⚪', tone: 'bg-gray-100 text-gray-500 border-gray-200' },
  { key: 'nutrir_mais_tarde', label: 'Nutrir mais tarde', emoji: '⏸', tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  { key: 'nao_prospectar', label: 'Não prospectar', emoji: '🚫', tone: 'bg-gray-200 text-gray-600 border-gray-300' },
];

export const TEMPERATURA_LABELS: Record<string, string> = Object.fromEntries(
  TEMPERATURA_RELACIONAMENTO.map((t) => [t.key, t.label]),
);
export const TEMPERATURA_INFO: Record<string, { label: string; emoji: string; tone: string }> = Object.fromEntries(
  TEMPERATURA_RELACIONAMENTO.map((t) => [t.key, { label: t.label, emoji: t.emoji, tone: t.tone }]),
);

// Estágio do relacionamento (NÃO substitui o estágio de oportunidade comercial)
export const ESTAGIO_RELACIONAMENTO: { key: string; label: string }[] = [
  { key: 'identificado', label: 'Identificado' },
  { key: 'primeiro_contato', label: 'Primeiro contato' },
  { key: 'conversa_iniciada', label: 'Conversa iniciada' },
  { key: 'demonstrou_interesse', label: 'Demonstrou interesse' },
  { key: 'reuniao_sugerida', label: 'Reunião sugerida' },
  { key: 'reuniao_agendada', label: 'Reunião agendada' },
  { key: 'reuniao_adiada', label: 'Reunião adiada' },
  { key: 'remarcar_reuniao', label: 'Remarcar reunião' },
  { key: 'reuniao_realizada', label: 'Reunião realizada' },
  { key: 'relacionamento_ativo', label: 'Relacionamento ativo' },
  { key: 'projeto_identificado', label: 'Projeto identificado' },
  { key: 'parceiro_recorrente', label: 'Parceiro recorrente' },
  { key: 'nutrir', label: 'Nutrir' },
  { key: 'encerrado', label: 'Encerrado' },
];

export const ESTAGIO_RELACIONAMENTO_LABELS: Record<string, string> = Object.fromEntries(
  ESTAGIO_RELACIONAMENTO.map((e) => [e.key, e.label]),
);

// Canais de relacionamento (memória única, sem silos)
export const CANAIS_RELACIONAMENTO: { key: string; label: string; icon: string }[] = [
  { key: 'whatsapp_normal', label: 'WhatsApp Normal', icon: 'ri-whatsapp-line' },
  { key: 'whatsapp_business', label: 'WhatsApp Business', icon: 'ri-whatsapp-line' },
  { key: 'instagram', label: 'Instagram', icon: 'ri-instagram-line' },
  { key: 'ligacao', label: 'Ligação', icon: 'ri-phone-line' },
  { key: 'reuniao', label: 'Reunião', icon: 'ri-team-line' },
  { key: 'evento', label: 'Evento', icon: 'ri-calendar-event-line' },
  { key: 'visita', label: 'Visita', icon: 'ri-building-2-line' },
  { key: 'showroom', label: 'Showroom', icon: 'ri-store-2-line' },
  { key: 'linkedin', label: 'LinkedIn', icon: 'ri-linkedin-box-line' },
  { key: 'indicacao', label: 'Indicação', icon: 'ri-user-heart-line' },
  { key: 'email', label: 'Email', icon: 'ri-mail-line' },
  { key: 'telefone', label: 'Telefone', icon: 'ri-phone-line' },
  { key: 'outro', label: 'Outro', icon: 'ri-more-line' },
];

export const CANAIS_LABELS: Record<string, string> = Object.fromEntries(
  CANAIS_RELACIONAMENTO.map((c) => [c.key, c.label]),
);

// Estados de "ponta solta" (promessas / compromissos futuros não resolvidos)
export const PONTAS_STATUS: { key: string; label: string; tone: string }[] = [
  { key: 'pendente', label: 'Pendente', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'agendada', label: 'Agendada', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'concluida', label: 'Concluída', tone: 'bg-gray-100 text-gray-600 border-gray-200' },
  { key: 'adiada', label: 'Adiada', tone: 'bg-orange-50 text-orange-600 border-orange-200' },
  { key: 'sem_resposta', label: 'Sem resposta', tone: 'bg-red-50 text-red-600 border-red-200' },
  { key: 'cancelada', label: 'Cancelada', tone: 'bg-gray-100 text-gray-400 border-gray-200' },
];

export const PONTAS_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  PONTAS_STATUS.map((s) => [s.key, s.label]),
);

// Integrações/Conectores (status real — nunca integração falsa)
export interface ConectorStatus {
  key: string;
  label: string;
  status: 'conectado' | 'aguardando' | 'erro' | 'nao_disponivel';
  ultima_sincronizacao: string | null;
}

// Conectores disponíveis/preparados — todos "aguardando conexão" até autorização real.
export const CONECTORES: { key: string; label: string; icon: string; desc: string }[] = [
  { key: 'whatsapp_normal', label: 'WhatsApp Normal', icon: 'ri-whatsapp-line', desc: 'API oficial / conector autorizado (webhook).' },
  { key: 'whatsapp_business', label: 'WhatsApp Business', icon: 'ri-whatsapp-line', desc: 'API oficial / conector autorizado (webhook).' },
  { key: 'instagram', label: 'Instagram', icon: 'ri-instagram-line', desc: 'Instagram profissional — sinal de relacionamento.' },
  { key: 'google_calendar', label: 'Google Calendar', icon: 'ri-calendar-line', desc: 'Cruzamento de viagem/deslocamento com contatos.' },
  { key: 'linkedin', label: 'LinkedIn', icon: 'ri-linkedin-box-line', desc: 'Preparado para futura integração.' },
];

export interface PontaSolta {
  id: number;
  descricao: string;
  origem: string | null;
  contato_id: number | null;
  empresa_id: number | null;
  contato_nome: string | null;
  empresa_nome: string | null;
  data_conversa: string | null;
  responsavel: string | null;
  janela_tempo: string | null;
  data_sugerida: string | null;
  status: string;
  proxima_acao: string | null;
}

export interface Viagem {
  id: number;
  cidade: string;
  uf: string | null;
  data_inicio: string | null;
  data_fim: string | null;
  motivo: string | null;
}

export interface RelacionamentoQuente {
  entidade_tipo: 'contato' | 'empresa';
  entidade_id: number;
  contato_id: number | null;
  empresa_id: number | null;
  nome: string;
  alias: string | null;
  empresa: string | null;
  papel: string | null;
  cidade: string | null;
  uf: string | null;
  temperatura: string;
  estagio: string | null;
  ultimo_contato: string | null;
  ultimo_canal: string | null;
  situacao: string | null;
  pendencia: string | null;
  proxima_acao: string | null;
  proxima_acao_data: string | null;
  sem_proxima_acao: boolean;
  score: number | null;
  score_tipo: string | null;
}